<template>
  <div class="story-record-detail-page">
    <header class="detail-header">
      <button class="back-btn" @click="goBack">‹</button>
      <div class="header-title">
        <h1>跟读详情</h1>
        <p v-if="record">{{ record.bookTitle }}</p>
      </div>
    </header>

    <div v-if="loading" class="state-card">
      <div class="state-icon">⏳</div>
      <p>正在加载记录...</p>
    </div>

    <div v-else-if="!record" class="state-card">
      <div class="state-icon">📖</div>
      <h2>没有找到记录</h2>
      <p>这条跟读记录可能已经不存在。</p>
      <button class="primary-btn" @click="goBack">返回学习记录</button>
    </div>

    <main v-else class="detail-content">
      <section class="summary-panel">
        <div class="summary-main">
          <span class="book-badge">{{ record.bookTitle }}</span>
          <h2>完成 {{ record.lineCount }} 句跟读</h2>
          <p>{{ formatTime(record.completedAt) }}</p>
        </div>
        <div
          class="score-badge"
          :style="{ background: getScoreColor(record.totalScore) }"
        >
          {{ record.totalScore }}
        </div>
      </section>

      <section class="line-section">
        <div class="section-header">
          <span>句子明细</span>
          <span>{{ lineResults.length }} 句</span>
        </div>

        <div
          v-for="(line, index) in lineResults"
          :key="line.resultId || line.lineId || index"
          class="line-card"
        >
          <div class="line-top">
            <span class="line-index">{{ index + 1 }}</span>
            <span
              class="line-score"
              :style="{ color: getPlainScoreColor(line.overall) }"
            >
              {{ line.overall }} 分
            </span>
          </div>
          <p class="line-text">{{ line.text }}</p>
        </div>
      </section>
    </main>
  </div>
</template>

<script>
import { getStoryPracticeRecords } from '@/api/book'
import { ensureWechatOpenid } from '@/utils/wechat-auth'

export default {
  name: 'StoryRecordDetail',
  data() {
    return {
      record: null,
      loading: false
    }
  },
  computed: {
    lineResults() {
      return this.record && this.record.lineResults ? this.record.lineResults : []
    }
  },
  created() {
    this.loadRecord()
  },
  methods: {
    async loadRecord() {
      this.loading = true
      try {
        await ensureWechatOpenid(this.$route)
        const res = await getStoryPracticeRecords()
        const records = res.data || []
        this.record = records.find(item => String(item.studyRecordId) === String(this.$route.params.id)) || null
      } catch (error) {
        console.error('获取跟读详情失败:', error)
        this.record = null
      } finally {
        this.loading = false
      }
    },
    getScoreColor(score) {
      if (score >= 90) return 'linear-gradient(135deg, #4CAF50 0%, #81C784 100%)'
      if (score >= 70) return 'linear-gradient(135deg, #FF9800 0%, #FFB74D 100%)'
      return 'linear-gradient(135deg, #F44336 0%, #E57373 100%)'
    },
    getPlainScoreColor(score) {
      if (score >= 90) return '#43A047'
      if (score >= 70) return '#F57C00'
      return '#E53935'
    },
    formatTime(timeStr) {
      const date = new Date(timeStr)
      const month = `${date.getMonth() + 1}`.padStart(2, '0')
      const day = `${date.getDate()}`.padStart(2, '0')
      const hour = `${date.getHours()}`.padStart(2, '0')
      const minute = `${date.getMinutes()}`.padStart(2, '0')
      return `${date.getFullYear()}-${month}-${day} ${hour}:${minute}`
    },
    goBack() {
      this.$router.push('/records')
    }
  }
}
</script>

<style scoped>
.story-record-detail-page {
  min-height: 100vh;
  background: linear-gradient(180deg, #E8F5E9 0%, #C8E6C9 100%);
  padding-bottom: 24px;
}

.detail-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 18px 15px 12px;
}

.back-btn {
  width: 36px;
  height: 36px;
  border: none;
  border-radius: 50%;
  background: #fff;
  color: #2E7D32;
  font-size: 30px;
  line-height: 30px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.08);
}

.header-title h1 {
  font-size: 22px;
  color: #2E7D32;
  margin: 0;
}

.header-title p {
  font-size: 13px;
  color: #78909C;
  margin: 4px 0 0;
}

.detail-content {
  padding: 0 15px;
}

.summary-panel,
.state-card {
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.06);
}

.summary-panel {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
  margin-bottom: 18px;
}

.summary-main {
  flex: 1;
}

.book-badge {
  display: inline-block;
  background: #F3E5F5;
  color: #7B1FA2;
  padding: 3px 9px;
  border-radius: 10px;
  font-size: 11px;
  margin-bottom: 8px;
}

.summary-main h2 {
  font-size: 18px;
  color: #333;
  margin: 0 0 6px;
}

.summary-main p {
  font-size: 12px;
  color: #999;
  margin: 0;
}

.score-badge {
  width: 52px;
  height: 52px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 18px;
  font-weight: 700;
}

.line-section {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: #2E7D32;
  font-size: 14px;
  font-weight: 600;
  padding: 0 2px;
}

.line-card {
  background: #fff;
  border-radius: 12px;
  padding: 14px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.06);
}

.line-top {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.line-index {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: #E8F5E9;
  color: #2E7D32;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 700;
}

.line-score {
  font-size: 14px;
  font-weight: 700;
}

.line-text {
  font-size: 15px;
  line-height: 1.6;
  color: #333;
  margin: 0;
  word-break: break-word;
}

.state-card {
  margin: 24px 15px 0;
  padding: 34px 20px;
  text-align: center;
}

.state-icon {
  font-size: 48px;
  margin-bottom: 10px;
}

.state-card h2 {
  font-size: 18px;
  color: #333;
  margin: 0 0 8px;
}

.state-card p {
  font-size: 14px;
  color: #888;
  margin: 0 0 18px;
}

.primary-btn {
  border: none;
  border-radius: 22px;
  background: #FF6B6B;
  color: #fff;
  font-size: 14px;
  padding: 10px 22px;
}
</style>



// WEBPACK FOOTER //
// src/views/StoryRecordDetail.vue